import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VoiceQuizRunner, { VoiceQuizAnswer } from '../components/Abacus/VoiceQuizRunner';
import { useAbacusFocus } from '../context/AbacusFocusContext';
import VirtualAbacus from '../components/Abacus/VirtualAbacus';
import abacusService, {
  AbacusAssessmentView,
  AbacusGateResourceStatus,
  AbacusTopicDetailsDTO,
  TutorSessionOffer,
  VoiceAttemptResult,
} from '../services/abacusService';
import { assessmentRuleText } from '../utils/abacusCourseHome';
import {
  enqueueAttempt,
  flushQueuedAttempts,
  readCachedTopic,
  SyncStatus,
} from '../pwa/offlineBundle';
import {
  accumulateForegroundPlayback,
  playbackQualifies,
  type PlaybackSample,
} from '../utils/theoryPlayback';

const MIN_MARK_SECONDS = 5;

const AbacusTopicPage: React.FC = () => {
  const { courseId, topicId } = useParams<{ courseId: string; topicId: string }>();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState<AbacusTopicDetailsDTO | null>(null);
  const [enrollmentId, setEnrollmentId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusMode, setFocusMode] = useState(mode === 'quiz');
  const { setFocusActive } = useAbacusFocus();
  const autoStartRef = useRef(false);
  const [softGateOpen, setSoftGateOpen] = useState(false);
  const [softGateMessage, setSoftGateMessage] = useState<string | null>(null);
  const [practiceQuestions, setPracticeQuestions] = useState<
    NonNullable<AbacusTopicDetailsDTO['practiceQuiz']>['questions'] | null
  >(null);
  const [voiceResult, setVoiceResult] = useState<VoiceAttemptResult | null>(null);
  const [assessment, setAssessment] = useState<AbacusAssessmentView | null>(null);
  const [pendingAnswers, setPendingAnswers] = useState<VoiceQuizAnswer[] | null>(null);
  const [savingAttempt, setSavingAttempt] = useState(false);
  const [resourceOpenSince, setResourceOpenSince] = useState<Record<number, number>>({});
  const [now, setNow] = useState(() => Date.now());
  const mediaRefs = useRef<Record<number, HTMLMediaElement | null>>({});
  const playbackRef = useRef<Record<number, PlaybackSample>>({});
  const recordingRef = useRef<Set<number>>(new Set());
  const [tutorOffer, setTutorOffer] = useState<TutorSessionOffer | null>(null);
  const [tutorWhen, setTutorWhen] = useState('');
  const [tutorJoinUrl, setTutorJoinUrl] = useState<string | null>(null);
  const [tutorMessage, setTutorMessage] = useState<string | null>(null);
  const [servedFromCache, setServedFromCache] = useState(false);
  const [queuedClientId, setQueuedClientId] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [testWeekReadOnly, setTestWeekReadOnly] = useState(false);

  const parsedCourseId = Number(courseId);
  const parsedTopicId = Number(topicId);

  useEffect(() => {
    if (!enrollmentId || !navigator.onLine || typeof abacusService.getTutorSessionOffer !== 'function') return;
    let cancelled = false;
    abacusService.getTutorSessionOffer(enrollmentId).then((offer) => {
      if (!cancelled) setTutorOffer(offer);
    }).catch(() => {
      if (!cancelled) setTutorOffer(null);
    });
    return () => {
      cancelled = true;
    };
  }, [enrollmentId]);

  const requestTutorSession = async () => {
    if (!enrollmentId || !parsedTopicId) return;
    if (!tutorOffer?.bookable) {
      setTutorMessage(
        'Tutor sessions are part of tutor-aided support. Your lessons, practice, Test Weeks, and abacus stay available.'
      );
      setTutorJoinUrl(null);
      return;
    }
    try {
      const booked = await abacusService.bookTutorSession(enrollmentId, {
        scheduledAt: tutorWhen,
        plannedTopicIds: [parsedTopicId],
      });
      setTutorJoinUrl(booked.joinUrl ?? null);
      setTutorMessage(null);
    } catch {
      setTutorMessage('Could not book a tutor session.');
    }
  };

  const reloadTopic = useCallback(async () => {
    if (!parsedCourseId || !parsedTopicId) return;
    const [structure, data] = await Promise.all([
      abacusService.getStudentCourseStructure(parsedCourseId),
      abacusService.getStudentTopic(parsedCourseId, parsedTopicId),
    ]);
    setEnrollmentId(structure.enrollmentId);
    setTopic(data);
    setServedFromCache(false);
  }, [parsedCourseId, parsedTopicId]);

  const applyCachedTopic = useCallback(async () => {
    const cached = await readCachedTopic(parsedCourseId, parsedTopicId);
    if (!cached) return false;
    setEnrollmentId(cached.enrollmentId);
    setTopic({
      topicId: cached.topicId,
      title: cached.title,
      levelCode: '',
      category: cached.category,
      contentMode: cached.contentMode as AbacusTopicDetailsDTO['contentMode'],
      contentViewed: true,
      theoryContent: cached.theoryContent,
        resources: cached.resources.map((resource) => ({
          id: resource.resourceId,
          type: resource.type ?? 'IMAGE',
          url: resource.url ?? '',
          title: resource.title,
        })),
      gateStatus: {
        topicFullyViewed: true,
        resources: cached.resources.map((resource) => ({
          resourceId: resource.resourceId,
          type: resource.type ?? 'IMAGE',
          title: resource.title,
          url: resource.url,
          consumed: false,
        })),
      },
      interactive: cached.interactive,
      practiceQuiz: cached.practiceQuestions.length
        ? { quizId: cached.topicId, title: cached.title, questions: cached.practiceQuestions }
        : undefined,
      testWeekGatePreview: cached.isTestWeek
        ? { prerequisiteTopics: [], canStart: false, missingTopicIds: [] }
        : undefined,
    });
    if (cached.practiceQuestions.length) {
      setPracticeQuestions(cached.practiceQuestions);
    }
    setServedFromCache(true);
    return true;
  }, [parsedCourseId, parsedTopicId]);

  useEffect(() => {
    setFocusActive(focusMode);
    return () => setFocusActive(false);
  }, [focusMode, setFocusActive]);

  useEffect(() => {
    const loadTopic = async () => {
      if (!parsedCourseId || !parsedTopicId) return;
      try {
        setLoading(true);
        if (!navigator.onLine) {
          const cached = await applyCachedTopic();
          if (!cached) setError('Download this level before opening it offline.');
          return;
        }
        await reloadTopic();
      } catch (err: unknown) {
        const cached = await applyCachedTopic().catch(() => false);
        if (cached) return;
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setError(message || 'Failed to load topic.');
      } finally {
        setLoading(false);
      }
    };

    loadTopic();
  }, [parsedCourseId, parsedTopicId, reloadTopic, applyCachedTopic]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        Object.values(mediaRefs.current).forEach((el) => el?.pause());
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const markResourceOpen = (resourceId: number) => {
    setResourceOpenSince((prev) =>
      prev[resourceId] ? prev : { ...prev, [resourceId]: Date.now() }
    );
  };

  const dwellSeconds = (resourceId: number) => {
    const started = resourceOpenSince[resourceId];
    if (!started) return 0;
    return Math.floor((now - started) / 1000);
  };

  const canMarkStatic = (resourceId: number) => dwellSeconds(resourceId) >= MIN_MARK_SECONDS;

  const markLabel = (label: string, resource: AbacusGateResourceStatus) => {
    if (resource.consumed) return label;
    const remaining = MIN_MARK_SECONDS - dwellSeconds(resource.resourceId);
    if (!resourceOpenSince[resource.resourceId] || remaining <= 0) return label;
    return `${label} in ${remaining}s`;
  };

  const recordConsumption = async (
    resourceId: number,
    consumptionMethod: string,
    dwell: number
  ) => {
    if (!enrollmentId) return;
    await abacusService.recordContentView(parsedCourseId, parsedTopicId, {
      enrollmentId,
      resourceId,
      consumptionMethod,
      dwellSeconds: dwell,
    });
    await reloadTopic();
  };

  const handleMediaProgress = async (resource: AbacusGateResourceStatus, el: HTMLMediaElement) => {
    if (resource.consumed || recordingRef.current.has(resource.resourceId)) return;
    const previous = playbackRef.current[resource.resourceId] ?? {
      foregroundSeconds: 0,
      lastMediaTime: null,
    };
    const next = accumulateForegroundPlayback(
      previous,
      el.currentTime,
      document.visibilityState === 'visible',
      el.paused
    );
    playbackRef.current[resource.resourceId] = next;
    const duration = el.duration;
    const ratio = Number.isFinite(duration) && duration > 0 ? next.foregroundSeconds / duration : 0;
    if (!playbackQualifies(next.foregroundSeconds, duration, el.ended)) return;
    const reachedEnd = el.ended && ratio < 0.8;
    const method =
      resource.type === 'AUDIO'
        ? reachedEnd
          ? 'AUDIO_COMPLETE'
          : 'AUDIO_80PCT'
        : reachedEnd
          ? 'VIDEO_COMPLETE'
          : 'VIDEO_80PCT';
    recordingRef.current.add(resource.resourceId);
    try {
      await recordConsumption(resource.resourceId, method, Math.floor(next.foregroundSeconds));
    } finally {
      recordingRef.current.delete(resource.resourceId);
    }
  };

  const readApiError = (err: unknown, fallback: string) => {
    if (err && typeof err === 'object' && 'response' in err) {
      const data = (err as {
        response?: {
          data?: {
            message?: string;
            data?: { missingPrerequisiteTopics?: { title?: string }[] };
          };
        };
      }).response?.data;
      const titles = data?.data?.missingPrerequisiteTopics
        ?.map((topic) => topic.title)
        .filter((title): title is string => Boolean(title));
      if (titles && titles.length > 0) {
        return `Complete these topics before Test Week: ${titles.join(', ')}`;
      }
      if (data?.message) return data.message;
    }
    return fallback;
  };

  const startPracticeFlow = async (overrideSoftGate: boolean) => {
    if (!enrollmentId) return;
    setFocusMode(true);
    setTestWeekReadOnly(false);
    if (!navigator.onLine) {
      const cached = await readCachedTopic(parsedCourseId, parsedTopicId);
      const questions = cached?.practiceQuestions ?? [];
      if (questions.length === 0) {
        setError('Download this level before practicing offline.');
        setFocusMode(false);
        return;
      }
      setPracticeQuestions(questions);
      setFocusMode(true);
      return;
    }
    try {
      const quiz = await abacusService.startPracticeQuiz(parsedTopicId, enrollmentId, overrideSoftGate);
      if (quiz.softGateRequired) {
        setSoftGateMessage(quiz.message ?? 'Watch the lesson first.');
        setSoftGateOpen(true);
        return;
      }
      setAssessment(quiz);
      if (quiz.questions && Array.isArray(quiz.questions)) {
        setVoiceResult(null);
        setPracticeQuestions(quiz.questions);
        setFocusMode(true);
      }
    } catch (err: unknown) {
      const cached = await readCachedTopic(parsedCourseId, parsedTopicId).catch(() => null);
      const offline = !navigator.onLine || (err && typeof err === 'object' && 'request' in err && !('response' in err && (err as { response?: unknown }).response));
      if (offline && cached?.practiceQuestions?.length) {
        setPracticeQuestions(cached.practiceQuestions);
        setFocusMode(true);
        setError(null);
        return;
      }
      setError(readApiError(err, 'Could not start practice.'));
    }
  };

  const startTestWeekFlow = async () => {
    if (!enrollmentId) return;
    if (!navigator.onLine) {
      const cached = await readCachedTopic(parsedCourseId, parsedTopicId);
      setTestWeekReadOnly(true);
      setError('Test Week is submitted online. You can read the cached questions here.');
      if (cached?.testWeekQuestions?.length) {
        setPracticeQuestions(cached.testWeekQuestions.map((question) => ({
          ...question,
          questionType: question.questionType ?? 'MENTAL_ARITHMETIC',
        })));
        setFocusMode(true);
      }
      return;
    }
    setFocusMode(true);
    try {
      const quiz = await abacusService.startTestWeek(parsedTopicId, enrollmentId);
      setAssessment(quiz);
      if (quiz.questions && Array.isArray(quiz.questions)) {
        setVoiceResult(null);
        setPracticeQuestions(quiz.questions);
        setFocusMode(true);
      }
    } catch (err: unknown) {
      setError(readApiError(err, 'Test Week is blocked until prerequisite lessons are complete.'));
    }
  };

  useEffect(() => {
    if (mode !== 'quiz' || loading || !topic || !enrollmentId || autoStartRef.current) return;
    autoStartRef.current = true;
    if (topic.contentMode !== 'DRILL') {
      setFocusMode(false);
      return;
    }
    if (topic.testWeekGatePreview && !topic.testWeekGatePreview.canStart) {
      setFocusMode(false);
      return;
    }
    if (topic.testWeekGatePreview) {
      void startTestWeekFlow();
    } else {
      void startPracticeFlow(false);
    }
  }, [mode, loading, topic, enrollmentId]);

  const leaveDrill = () => {
    setFocusMode(false);
    setVoiceResult(null);
    navigate(`/student-courses/${parsedCourseId}/abacus`);
  };

  const persistVoiceAttempt = async (answers: VoiceQuizAnswer[]) => {
    if (!enrollmentId) return;
    if (testWeekReadOnly) {
      setError('Test Week is submitted online.');
      return;
    }
    setPendingAnswers(answers);
    setSavingAttempt(true);
    setError(null);
    if (!navigator.onLine) {
      const clientAttemptId = crypto.randomUUID();
      await enqueueAttempt({
        clientAttemptId,
        courseId: parsedCourseId,
        topicId: parsedTopicId,
        enrollmentId,
        synced: false,
        answers: answers.map((answer) => ({
          questionId: answer.questionId,
          promptSpoken: answer.promptSpoken,
          expectedAnswer: answer.expectedAnswer,
          answerRaw: answer.answerRaw,
          inputMethod: 'TYPED',
          sttConfidence: null,
          tolerance: answer.tolerance,
        })),
      });
      setQueuedClientId(clientAttemptId);
      setPendingAnswers(null);
      setSavingAttempt(false);
      return;
    }
    try {
      const saved = await abacusService.submitVoiceAttempt(parsedTopicId, enrollmentId, {
        answers: answers.map((answer) => ({
          questionId: answer.questionId,
          promptSpoken: answer.promptSpoken,
          expectedAnswer: answer.expectedAnswer,
          answerRaw: answer.answerRaw,
          inputMethod: answer.inputMethod,
          sttConfidence: answer.sttConfidence ?? null,
          tolerance: answer.tolerance,
        })),
      });
      setVoiceResult(saved);
      setPendingAnswers(null);
    } catch (err: unknown) {
      const offline = !navigator.onLine || (err && typeof err === 'object' && 'request' in err && !('response' in err && (err as { response?: unknown }).response));
      if (offline) {
        const clientAttemptId = crypto.randomUUID();
        await enqueueAttempt({
          clientAttemptId,
          courseId: parsedCourseId,
          topicId: parsedTopicId,
          enrollmentId,
          synced: false,
          answers: answers.map((answer) => ({
            questionId: answer.questionId,
            promptSpoken: answer.promptSpoken,
            expectedAnswer: answer.expectedAnswer,
            answerRaw: answer.answerRaw,
            inputMethod: 'TYPED',
            sttConfidence: null,
            tolerance: answer.tolerance,
          })),
        });
        setQueuedClientId(clientAttemptId);
        setPendingAnswers(null);
        setSavingAttempt(false);
        setError(null);
        return;
      }
      setError(readApiError(err, 'Could not save this drill.'));
      setSavingAttempt(false);
      return;
    }
    setSavingAttempt(false);
  };

  useEffect(() => {
    const onOnline = () => {
      void flushQueuedAttempts().then((status) => {
        if (status) setSyncStatus(status);
      }).catch(() => undefined);
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  if (loading) {
    return (
      <Box minHeight="60vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!topic) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error ?? 'Topic unavailable.'}</Alert>
      </Container>
    );
  }

  const gateResources = topic.gateStatus?.resources ?? [];
  const hasVirtualAbacus =
    topic.category === 'ABACUS_FINGER_THEORY' || topic.interactive?.type === 'VIRTUAL_ABACUS';
  const isTestWeek = topic.contentMode === 'DRILL' && topic.testWeekGatePreview != null;
  const quizQuestions = focusMode
    ? (practiceQuestions ?? [])
    : (practiceQuestions ?? topic.practiceQuiz?.questions ?? []);

  return (
    <Container
      maxWidth="md"
      data-testid={focusMode ? 'abacus-focus-mode' : undefined}
      sx={
        focusMode
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 1200,
              bgcolor: 'background.default',
              overflow: 'auto',
              py: 2,
            }
          : { py: 4 }
      }
    >
      {focusMode ? (
        <Button data-testid="abacus-focus-leave" onClick={leaveDrill} sx={{ mb: 2 }}>
          Leave drill
        </Button>
      ) : (
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/student-courses/${parsedCourseId}/abacus`)}
        >
          Back to course
        </Button>
      </Stack>
      )}

      {!focusMode && error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!focusMode && (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" fontWeight={700} mb={1}>
          {topic.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {topic.levelCode} • {topic.category.replace(/_/g, ' ')}
        </Typography>
        {topic.gateStatus && (
          <Typography variant="body2" sx={{ mt: 1 }} color={topic.gateStatus.topicFullyViewed ? 'success.main' : 'text.secondary'}>
            {topic.gateStatus.topicFullyViewed ? 'Lesson complete for gating' : 'Complete all lesson resources below'}
          </Typography>
        )}
      </Paper>
      )}

      {!focusMode ? (
        <Stack spacing={2}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" mb={2}>
              Learn
            </Typography>
            {servedFromCache && (
              <Typography data-testid="abacus-offline-lesson" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                {topic.theoryContent || topic.title}
              </Typography>
            )}
            {gateResources.length === 0 ? (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {topic.theoryContent || 'Lesson media will appear here when uploaded.'}
              </Typography>
            ) : (
              <Stack spacing={3}>
                {gateResources.map((resource) => (
                  <Box key={resource.resourceId}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {resource.title ?? resource.type}
                      {resource.consumed ? ' ✓' : ''}
                    </Typography>
                    {resource.consumptionHint && (
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        {resource.consumptionHint}
                      </Typography>
                    )}
                    {(resource.type === 'VIDEO' || resource.type === 'AUDIO') && resource.url && (
                      <>
                        {resource.type === 'VIDEO' ? (
                          <Box
                            component="video"
                            controls
                            sx={{ width: '100%', maxHeight: 360 }}
                            src={resource.url}
                            ref={(el) => {
                              mediaRefs.current[resource.resourceId] = el;
                            }}
                            onLoadedMetadata={() => markResourceOpen(resource.resourceId)}
                            onTimeUpdate={(e) =>
                              handleMediaProgress(resource, e.currentTarget as HTMLMediaElement)
                            }
                            onEnded={(e) =>
                              handleMediaProgress(resource, e.currentTarget as HTMLMediaElement)
                            }
                          />
                        ) : (
                          <Box
                            component="audio"
                            controls
                            sx={{ width: '100%' }}
                            src={resource.url}
                            ref={(el) => {
                              mediaRefs.current[resource.resourceId] = el;
                            }}
                            onLoadedMetadata={() => markResourceOpen(resource.resourceId)}
                            onTimeUpdate={(e) =>
                              handleMediaProgress(resource, e.currentTarget as HTMLMediaElement)
                            }
                            onEnded={(e) =>
                              handleMediaProgress(resource, e.currentTarget as HTMLMediaElement)
                            }
                          />
                        )}
                      </>
                    )}
                    {resource.type === 'PDF' && resource.url && (
                      <Stack spacing={1}>
                        <Button variant="outlined" href={resource.url} target="_blank" rel="noreferrer" onClick={() => markResourceOpen(resource.resourceId)}>
                          Open PDF
                        </Button>
                        {resourceOpenSince[resource.resourceId] && (
                          <Box
                            component="iframe"
                            title={resource.title ?? 'Lesson PDF'}
                            src={resource.url}
                            sx={{ width: '100%', height: 360, border: 0 }}
                          />
                        )}
                        <Button
                          variant="contained"
                          disabled={resource.consumed || !canMarkStatic(resource.resourceId)}
                          onClick={() =>
                            recordConsumption(resource.resourceId, 'MARK_READ', Math.max(5, dwellSeconds(resource.resourceId)))
                          }
                        >
                          {markLabel('Mark as read', resource)}
                        </Button>
                      </Stack>
                    )}
                    {resource.type === 'IMAGE' && resource.url && (
                      <Stack spacing={1}>
                        <Box
                          component="img"
                          src={resource.url}
                          alt={resource.title ?? 'Lesson image'}
                          sx={{ maxWidth: '100%', borderRadius: 1 }}
                          onLoad={() => markResourceOpen(resource.resourceId)}
                        />
                        <Button
                          variant="contained"
                          disabled={resource.consumed || !canMarkStatic(resource.resourceId)}
                          onClick={() =>
                            recordConsumption(resource.resourceId, 'MARK_VIEWED', Math.max(5, dwellSeconds(resource.resourceId)))
                          }
                        >
                          {markLabel('Mark as viewed', resource)}
                        </Button>
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>

          {topic.testWeekGatePreview && !topic.testWeekGatePreview.canStart && (
            <Alert severity="info">
              <Typography variant="subtitle2" mb={1}>
                Complete these topics before Test Week:
              </Typography>
              <List dense>
                {topic.testWeekGatePreview.prerequisiteTopics
                  .filter((p) => !p.fullyViewed)
                  .map((p) => (
                    <ListItem key={p.topicId} disablePadding>
                      <ListItemText primary={p.title} />
                      <Button size="small" onClick={() => navigate(`/student-courses/${parsedCourseId}/abacus/topics/${p.topicId}`)}>
                        Open
                      </Button>
                    </ListItem>
                  ))}
              </List>
            </Alert>
          )}

          {hasVirtualAbacus && (
            <VirtualAbacus columns={Math.max(2, topic.interactive?.columns ?? 2)} />
          )}

          <Paper variant="outlined" sx={{ p: 2 }} data-testid="tutor-session-request">
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Tutor session
            </Typography>
            {(tutorOffer?.upsell || tutorMessage) && (
              <Alert severity="info" data-testid="tutor-session-upsell" sx={{ mb: 1 }}>
                {tutorMessage
                  ?? 'Tutor sessions are part of tutor-aided support. Your lessons, practice, Test Weeks, and abacus stay available.'}
              </Alert>
            )}
            {tutorOffer?.bookable && (
              <TextField
                label="Session time"
                type="datetime-local"
                value={tutorWhen}
                onChange={(event) => setTutorWhen(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1 }}
              />
            )}
            <Button variant="outlined" onClick={() => void requestTutorSession()} disabled={!enrollmentId}>
              Request a tutor session
            </Button>
            {tutorJoinUrl && (
              <Typography variant="body2" sx={{ mt: 1 }} data-testid="tutor-session-join">
                {tutorJoinUrl}
              </Typography>
            )}
          </Paper>

          {isTestWeek ? (
            <Button variant="contained" color="secondary" onClick={() => startTestWeekFlow()}>
              Start Test Week
            </Button>
          ) : (
            <Button variant="contained" onClick={() => startPracticeFlow(false)} disabled={!enrollmentId}>
              Start practice
            </Button>
          )}
        </Stack>
      ) : (
        <Stack spacing={2}>
          {voiceResult ? (
            <Stack spacing={2}>
              <Alert
                severity={voiceResult.assessmentKind === 'TEST_WEEK' && voiceResult.passed === false ? 'warning' : 'success'}
                data-testid="abacus-assessment-result"
              >
                {voiceResult.assessmentKind === 'TEST_WEEK'
                  ? `Test Week ${voiceResult.passed ? 'passed' : 'not passed'}. Score ${voiceResult.score}% · pass mark ${voiceResult.minPassPercent}%. Attempt ${voiceResult.attemptsUsed} of ${voiceResult.maxAttempts}. The next level stays locked until later rules apply.`
                  : `Practice feedback: ${voiceResult.score}% · pass mark ${voiceResult.minPassPercent ?? assessment?.minPassPercent ?? 70}%. This does not unlock the next level.`}
              </Alert>
              <Button
                variant="outlined"
                data-testid="abacus-assessment-retry"
                onClick={() => {
                  if (isTestWeek) {
                    void startTestWeekFlow();
                  } else {
                    void startPracticeFlow(true);
                  }
                }}
              >
                Try again
              </Button>
              {voiceResult.answers.map((answer) => (
                <Paper key={answer.questionId} sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography>{answer.promptSpoken}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip data-testid="voice-quiz-input-method" label={answer.inputMethod} size="small" />
                      <Typography>
                        {answer.answerNormalized ?? 'Not a number'}
                        {answer.correct ? ' · correct' : ' · incorrect'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : focusMode && !practiceQuestions && !voiceResult ? (
            <Stack alignItems="center" py={6}>
              <CircularProgress data-testid="abacus-focus-loading" />
            </Stack>
          ) : quizQuestions.length > 0 ? (
            <Stack spacing={2}>
            {assessment && (
              <Alert severity="info" data-testid="abacus-assessment-label">
                {assessmentRuleText(assessment)}
              </Alert>
            )}
            <VoiceQuizRunner
              advancePauseMs={assessment?.interQuestionPauseMs ?? 800}
              typedDefault={!navigator.onLine || testWeekReadOnly}
              questions={quizQuestions
                .filter((question) => question.type === 'MENTAL_ARITHMETIC' || question.questionType === 'MENTAL_ARITHMETIC')
                .map((question) => ({
                  id: question.id ?? question.questionId ?? 'q',
                  content: question.promptSpoken ?? question.content ?? question.prompt ?? '',
                  correctAnswer: Number(question.correctAnswer ?? question.answer ?? 0),
                  points: question.points,
                  tolerance: question.tolerance,
                  options: (question.options as { tolerance?: number } | undefined) ?? undefined,
                }))}
              onComplete={(answers) => {
                void persistVoiceAttempt(answers);
              }}
            />
            </Stack>
          ) : (
            <Alert severity="info">No practice quiz is configured for this topic yet.</Alert>
          )}
          {queuedClientId && (
            <Alert
              severity="info"
              data-testid="abacus-offline-queued"
              data-client-attempt-id={queuedClientId}
            >
              Saved on this device. It will upload when you reconnect.
            </Alert>
          )}
          {syncStatus && (
            <Alert
              severity="success"
              data-testid="abacus-sync-status"
              data-client-attempt-id={syncStatus.clientAttemptId}
              data-attempt-id={String(syncStatus.attemptId)}
              data-duplicate={syncStatus.duplicate ? 'true' : 'false'}
            >
              Attempt synced
            </Alert>
          )}
          {savingAttempt && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography>Saving your answers…</Typography>
            </Stack>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {error && pendingAnswers && (
            <Button variant="contained" onClick={() => { void persistVoiceAttempt(pendingAnswers); }}>
              Try saving again
            </Button>
          )}
        </Stack>
      )}

      <Dialog open={softGateOpen} onClose={() => setSoftGateOpen(false)}>
        <DialogTitle>Watch lesson first?</DialogTitle>
        <DialogContent>
          <Typography>{softGateMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSoftGateOpen(false);
              setFocusMode(false);
            }}
          >
            Watch lesson first
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSoftGateOpen(false);
              startPracticeFlow(true);
            }}
          >
            Continue anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AbacusTopicPage;
